"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import {
  Files,
  Users,
  Gear,
  Question,
  Shield,
  CreditCard,
  Calendar,
  CurrencyDollar,
} from "phosphor-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTenantLimits } from "@/hooks/useTenantLimits";

interface MenuItem {
  href: string;
  label: string;
  icon: React.ReactElement;
  requiresFeature?: "enable_events" | "enable_compliance_review" | "enable_dues_management";
}

const links: MenuItem[] = [
  {
    href: "/admin/documents",
    label: "Documents",
    icon: <Files size={28} weight="duotone" />,
  },
  {
    href: "/admin/members",
    label: "Members",
    icon: <Users size={28} weight="duotone" />,
  },
  {
    href: "/admin/calendar",
    label: "Calendar",
    icon: <Calendar size={28} weight="duotone" />,
    requiresFeature: "enable_events",
  },
  {
    href: "/admin/compliance",
    label: "Compliance",
    icon: <Shield size={28} weight="duotone" />,
    requiresFeature: "enable_compliance_review",
  },
  {
    href: "/admin/dues",
    label: "Dues",
    icon: <CurrencyDollar size={28} weight="duotone" />,
    requiresFeature: "enable_dues_management",
  },
  {
    href: "/admin/subscription",
    label: "Subscription",
    icon: <CreditCard size={28} weight="duotone" />,
  },
  {
    href: "/admin/site",
    label: "Site",
    icon: <Gear size={28} weight="duotone" />,
  },
  {
    href: "/admin/help",
    label: "Help",
    icon: <Question size={28} weight="duotone" />,
  },
];

interface SidebarNavProps {
  tenantId?: string;
  memberType?: string | null;
}

export default function SidebarNav({ tenantId, memberType }: SidebarNavProps) {
  const pathname = usePathname();
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  
  const { limits, loading } = useTenantLimits(tenantId);

  // Debug: Log limits to see what we're getting
  useEffect(() => {
    if (limits) {
      console.log("SidebarNav - Tenant limits:", limits);
      console.log("SidebarNav - hide_subscriptions:", limits.hide_subscriptions);
    }
  }, [limits]);

  const showTooltip = (text: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      text,
      x: rect.left + rect.width + 8,
      y: rect.top + rect.height / 2,
    });
  };

  const hideTooltip = () => {
    setTooltip(null);
  };

  const isFeatureEnabled = (feature?: "enable_events" | "enable_compliance_review" | "enable_dues_management"): boolean => {
    if (!feature) return true;
    if (!limits) return false;
    return limits[feature];
  };

  const getDisabledTooltip = (link: MenuItem): string | null => {
    if (!link.requiresFeature || isFeatureEnabled(link.requiresFeature)) {
      return null;
    }
    
    const featureNames: Record<string, string> = {
      enable_events: "Calendar Events",
      enable_compliance_review: "Compliance Review",
      enable_dues_management: "Dues Management",
    };
    
    return `${featureNames[link.requiresFeature]} is not available in your current plan`;
  };

  // Filter out subscription menu item if hide_subscriptions is true
  const visibleLinks = links.filter((link) => {
    if (link.href === "/admin/subscription") {
      // Hide if hide_subscriptions is explicitly true
      if (limits?.hide_subscriptions === true) {
        console.log("SidebarNav - Hiding subscription menu item");
        return false;
      }
    }
    return true;
  });

  return (
    <>
      <aside className="bg-white border-r flex w-16">
        <div className="flex-1 flex flex-col">
          <nav className="flex-1 flex flex-col gap-2 px-2 py-8">
            {visibleLinks.map((link) => {
              const isActive = pathname.startsWith(link.href);
              const isDisabled = !isFeatureEnabled(link.requiresFeature);
              const disabledTooltip = getDisabledTooltip(link);
              
              if (isDisabled) {
                return (
                  <div
                    key={link.href}
                    className="flex items-center justify-center px-3 py-2 rounded-lg font-medium transition text-gray-300 cursor-not-allowed min-w-[64px] opacity-50"
                    onMouseEnter={(e) => showTooltip(disabledTooltip || link.label, e)}
                    onMouseLeave={hideTooltip}
                  >
                    {link.icon}
                  </div>
                );
              }
              
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`flex items-center justify-center px-3 py-2 rounded-lg font-medium transition text-gray-700 hover:bg-gray-100 min-w-[64px] ${
                    isActive ? "bg-gray-100 text-blue-700" : ""
                  }`}
                  onMouseEnter={(e) => showTooltip(link.label, e)}
                  onMouseLeave={hideTooltip}
                >
                  {link.icon}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded shadow-lg pointer-events-none whitespace-nowrap"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateY(-50%)",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </>
  );
}
